import { oktaAuth } from './okta-auth';
import { config } from '../config/env';

// Unlike the Admin UI (whose bundled nginx reverse-proxies /api/admin/* to
// the adapter server-side), this app calls the gateway's public URL
// directly from the browser — the gateway's CORS middleware already
// allows this (see CORS_ALLOWED_ORIGINS in okta_agent_proxy/app.py). That
// keeps this UI's nginx config to plain static-file serving, no reverse
// proxy / DNS-resolver complexity needed.
const API_BASE_URL = config.gatewayUrl.replace(/\/$/, '');

export interface SelfServiceResource {
  name: string;
  url: string;
  description: string;
  connection_type: string | null;
}

export interface SelfServiceAgent {
  agent_id: string;
  agent_name: string;
  resources: SelfServiceResource[];
}

export interface MyAccessResponse {
  agents: SelfServiceAgent[];
  // Only when true does /mcp/{agent_name}/{resource_name} (the agent-routing
  // dual routes in okta_agent_proxy/app.py) actually exist on the gateway.
  agent_routing_enabled: boolean;
}

export interface SelfServiceTool {
  name: string;
  description: string;
  sensitivity: string | null;
  category: string | null;
}

export interface SelfServiceToolResource {
  resource_name: string;
  tools: SelfServiceTool[];
}

export interface SelfServiceToolAgent {
  agent_id: string;
  agent_name: string;
  resources: SelfServiceToolResource[];
}

export interface MyToolsResponse {
  agents: SelfServiceToolAgent[];
}

export interface ToolRefreshResourceResult {
  resource_name: string | null;
  status: 'success' | 'failure';
  tools_observed?: number;
  added?: number;
  reappeared?: number;
  schema_changed?: number;
  unchanged?: number;
  soft_deleted?: number;
  reason?: string;
}

export interface ToolRefreshResponse {
  resources: ToolRefreshResourceResult[];
}

export interface OwnedResource {
  connection_id: string;
  resource_name: string;
  description: string;
  enabled: boolean;
}

export interface OwnedAgent {
  agent_id: string;
  agent_name: string;
  enabled: boolean;
  okta_ai_agent_id: string | null;
  resources: OwnedResource[];
}

export interface AgentOwnershipResponse {
  agents: OwnedAgent[];
}

export interface RequestableAgent {
  agent_id: string;
  agent_name: string;
  catalog_entry_id: string;
}

export interface RequestableAgentsResponse {
  agents: RequestableAgent[];
}

export interface AccessRequestResult {
  agent_id: string;
  request_id: string | null;
  status: string | null;
}

export interface PendingAccessRequest {
  agent_id: string;
  agent_name: string;
  request_url: string | null;
}

export interface PendingAccessRequestsResponse {
  requests: PendingAccessRequest[];
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function getToken(): Promise<string | null> {
  return oktaAuth.getAccessToken() || null;
}

async function selfServiceFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init?.headers as Record<string, string> | undefined),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${path}`, { ...init, headers });

  if (!response.ok) {
    const body = await response.json().catch(() => ({ message: response.statusText }));

    if (response.status === 401) {
      setTimeout(() => {
        oktaAuth.signOut();
      }, 500);
    }

    throw new ApiError(body.message || `Request to ${path} failed`, response.status);
  }

  return response.json();
}

export async function getMyAccess(): Promise<MyAccessResponse> {
  return selfServiceFetch<MyAccessResponse>('/api/self-service/my-access');
}

export async function getMyTools(): Promise<MyToolsResponse> {
  return selfServiceFetch<MyToolsResponse>('/api/self-service/my-tools');
}

export async function refreshMyTools(): Promise<ToolRefreshResponse> {
  const idToken = oktaAuth.getIdToken();
  if (!idToken) {
    throw new ApiError('No ID token available — please sign in again', 401);
  }
  return selfServiceFetch<ToolRefreshResponse>('/api/self-service/tools/refresh', {
    method: 'POST',
    body: JSON.stringify({ id_token: idToken }),
  });
}

export async function getAgentOwnership(): Promise<AgentOwnershipResponse> {
  return selfServiceFetch<AgentOwnershipResponse>('/api/self-service/agent-ownership');
}

export async function setAgentEnabled(agentId: string, enabled: boolean): Promise<{ agent_id: string; enabled: boolean }> {
  return selfServiceFetch(`/api/self-service/agents/${encodeURIComponent(agentId)}/enabled`, {
    method: 'POST',
    body: JSON.stringify({ enabled }),
  });
}

export async function setResourceConnectionEnabled(
  agentId: string,
  connectionId: string,
  enabled: boolean
): Promise<{ agent_id: string; connection_id: string; enabled: boolean }> {
  return selfServiceFetch(
    `/api/self-service/agents/${encodeURIComponent(agentId)}/resources/${encodeURIComponent(connectionId)}/enabled`,
    {
      method: 'POST',
      body: JSON.stringify({ enabled }),
    }
  );
}

export async function getRequestableAgents(): Promise<RequestableAgentsResponse> {
  return selfServiceFetch<RequestableAgentsResponse>('/api/self-service/request-access/agents');
}

export async function submitAccessRequest(
  agentId: string,
  justification: string
): Promise<AccessRequestResult> {
  return selfServiceFetch('/api/self-service/request-access', {
    method: 'POST',
    body: JSON.stringify({ agent_id: agentId, justification }),
  });
}

export async function getPendingAccessRequests(): Promise<PendingAccessRequestsResponse> {
  return selfServiceFetch<PendingAccessRequestsResponse>('/api/self-service/access-requests/pending');
}
